import {
  DprTableOfColumnsAccumulation,
  PreviousDpr,
} from '@dpr-details/models';
import { DprTableOfColumnDetail } from '@shared/models/dpr';
import {
  TableOfColumnItemWithValues,
  TableOfColumnWithData,
} from '@shared/models/project-setup';
import {
  CarryOverDefaultValue,
  getRowAnchorValue,
  isTableOfColumnItemCarryOverEnabled,
  normalizeCarryOverValue,
} from './accumulation.utils';

/**
 * Resolves the value of every accumulated item, per table and per anchor list
 * value. A table's rows can differ from one DPR to the next, so a row is matched
 * against the previous DPR through its anchor value rather than by id.
 *
 * A Formula source is read like any other: the previous DPR it is handed is
 * rebuilt from the DPR's previous values, which hold the formula's worked-out
 * value.
 */
export function computeTableOfColumnsAccumulation(
  tablesOfColumns: TableOfColumnWithData[],
  previousDpr: PreviousDpr | null
): Map<number, DprTableOfColumnsAccumulation> {
  const accumulations = new Map<number, DprTableOfColumnsAccumulation>();

  tablesOfColumns?.forEach((tableOfColumn) => {
    const anchorColumnId = tableOfColumn.anchorColumn?.anchorColumnId;
    const carryOverItems = tableOfColumn.tableOfColumnItems?.filter((item) =>
      isTableOfColumnItemCarryOverEnabled(item)
    );

    if (anchorColumnId == null || !carryOverItems?.length) {
      return;
    }

    const previousRows = mapPreviousRowsByAnchorValue(
      tableOfColumn,
      +anchorColumnId,
      previousDpr
    );

    accumulations.set(+tableOfColumn.id, {
      anchorColumnId: +anchorColumnId,
      carryOverItemIds: carryOverItems.map((item) => +item.id),
      valuesByAnchorValue: mapValuesByAnchorValue(
        tableOfColumn,
        +anchorColumnId,
        carryOverItems,
        previousRows
      ),
    });
  });

  return accumulations;
}

function mapPreviousRowsByAnchorValue(
  tableOfColumn: TableOfColumnWithData,
  anchorColumnId: number,
  previousDpr: PreviousDpr | null
): Map<string, DprTableOfColumnDetail> {
  const previousRows = new Map<string, DprTableOfColumnDetail>();

  previousDpr?.dprTableOfColumns
    ?.find(
      (dprTableOfColumn) =>
        dprTableOfColumn.tableOfColumnId === +tableOfColumn.id
    )
    ?.dprTableOfColumnsDetails?.forEach((detail) => {
      const anchorValue = getRowAnchorValue(detail, anchorColumnId);

      // The anchor is meant to identify a row uniquely, so a repeated value is a
      // data error. Keeping the first row makes the outcome independent of the
      // order the rows happen to arrive in.
      if (anchorValue != null && !previousRows.has(anchorValue)) {
        previousRows.set(anchorValue, detail);
      }
    });

  return previousRows;
}

function mapValuesByAnchorValue(
  tableOfColumn: TableOfColumnWithData,
  anchorColumnId: number,
  carryOverItems: TableOfColumnItemWithValues[],
  previousRows: Map<string, DprTableOfColumnDetail>
): Map<string, Map<number, string | null>> {
  const valuesByAnchorValue = new Map<string, Map<number, string | null>>();

  collectAnchorValues(tableOfColumn, anchorColumnId, previousRows).forEach(
    (anchorValue) => {
      const itemValues = new Map<number, string | null>();

      carryOverItems.forEach((item) =>
        itemValues.set(+item.id, resolveValue(item, anchorValue, previousRows))
      );

      valuesByAnchorValue.set(anchorValue, itemValues);
    }
  );

  return valuesByAnchorValue;
}

/**
 * The anchor list options to resolve a value for: the ones the anchor column
 * offers today, plus any the previous DPR used. The second half matters because a
 * list option can be deactivated while rows still reference it.
 */
function collectAnchorValues(
  tableOfColumn: TableOfColumnWithData,
  anchorColumnId: number,
  previousRows: Map<string, DprTableOfColumnDetail>
): Set<string> {
  const anchorValues = new Set<string>(previousRows.keys());

  tableOfColumn.tableOfColumnItems
    ?.find((item) => +item.id === anchorColumnId)
    ?.values?.forEach((itemValue) => anchorValues.add(String(itemValue.id)));

  return anchorValues;
}

function resolveValue(
  item: TableOfColumnItemWithValues,
  anchorValue: string,
  previousRows: Map<string, DprTableOfColumnDetail>
): string | null {
  const sourceItemId = +item.carryOver.carryOverItemId;

  // An item carrying over from itself can only ever read back its own initial
  // value: the cell is read-only, so nothing writes anything else into it.
  if (sourceItemId === +item.id) {
    return getInitialValue(item, anchorValue);
  }

  const previousRow = previousRows.get(anchorValue);

  // Either this is the first DPR, or the anchor value is new to the project — in
  // both cases the configured initial value is what seeds the column.
  if (!previousRow) {
    return getInitialValue(item, anchorValue);
  }

  return normalizeCarryOverValue(
    previousRow.dprTableOfColumnDetailItems?.find(
      (detailItem) => detailItem.tableOfColumnItemId === sourceItemId
    )?.value
  );
}

function getInitialValue(
  item: TableOfColumnItemWithValues,
  anchorValue: string
): string {
  const initialValue = item.carryOver?.anchorValues?.find(
    // Matched on the anchor list option, not on the anchor value record's own id.
    (carryOverAnchorValue) =>
      String(carryOverAnchorValue.itemDataTypeValueId) === anchorValue
  )?.initialValue;

  return initialValue == null ? CarryOverDefaultValue : String(initialValue);
}
